"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  LayoutDashboard, Users, Palette, Megaphone, PenSquare, CalendarDays,
  Mail, FileText, PanelsTopLeft, Settings, Sparkles, ListTodo,
  Receipt, BarChart3, Search as SearchIcon, MousePointerClick, FolderOpen,
  CheckCircle2, Star, Workflow, BookOpen, Newspaper, Clapperboard, Share2, type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { AJUSTES, MENU } from "@/lib/menu";

// Iconos de cada opción. La lista (y qué ve el equipo) vive en lib/menu.ts.
const ICONOS: Record<string, LucideIcon> = {
  "/": LayoutDashboard, "/clientes": Users, "/tareas": ListTodo,
  "/marca": Palette, "/campanas": Megaphone, "/contenido": PenSquare, "/articulos": BookOpen,
  "/calendario": CalendarDays, "/redes": Share2, "/influencers": Star, "/activos": FolderOpen,
  "/emails": Mail, "/presupuestos": FileText, "/landings": PanelsTopLeft, "/animaciones": Clapperboard,
  "/notas-prensa": Newspaper, "/aprobaciones": CheckCircle2,
  "/ads": MousePointerClick, "/seo": SearchIcon, "/informes": BarChart3,
  "/facturacion": Receipt, "/automatizaciones": Workflow,
};

/** `ocultas`: opciones que este usuario no ve (rol «equipo», lo decide el admin). */
export function AppSidebar({ ocultas = [] }: { ocultas?: string[] }) {
  const pathname = usePathname();
  const fuera = new Set(ocultas);
  const secciones = MENU
    .map((s) => ({ ...s, items: s.items.filter((i) => !fuera.has(i.href)) }))
    .filter((s) => s.items.length > 0);
  const activo = (href: string) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);

  return (
    <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col border-r bg-sidebar text-sidebar-foreground md:flex">
      <div className="flex h-14 items-center gap-2 border-b px-4">
        <div className="flex size-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
          <Sparkles className="size-4" />
        </div>
        <div className="leading-tight">
          <p className="text-sm font-semibold">Agencia Hub</p>
          <p className="text-[11px] text-muted-foreground">Centro de marketing</p>
        </div>
      </div>

      <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
        {secciones.map((s) => (
          <div key={s.titulo}>
            <p className="px-2 pb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              {s.titulo}
            </p>
            <ul className="space-y-0.5">
              {s.items.map((item) => {
                const Icono = ICONOS[item.href] ?? LayoutDashboard;
                return (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    className={cn(
                      "flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors",
                      activo(item.href)
                        ? "bg-primary/10 font-medium text-primary"
                        : "text-muted-foreground hover:bg-accent hover:text-foreground"
                    )}
                  >
                    <Icono className="size-4" />
                    {item.label}
                  </Link>
                </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {!fuera.has(AJUSTES.href) && (
      <div className="border-t p-3">
        <Link
          href="/ajustes"
          className={cn(
            "flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors",
            pathname.startsWith("/ajustes")
              ? "bg-primary/10 font-medium text-primary"
              : "text-muted-foreground hover:bg-accent hover:text-foreground"
          )}
        >
          <Settings className="size-4" />
          {AJUSTES.label}
        </Link>
      </div>
      )}
    </aside>
  );
}
